import { Localized } from "@fluent/react"
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import { useCallback, useState } from "react"
import { If } from "../../../../app/If"
import { store } from "../../../../app/store"
import { selectUserByName, User } from "../../../users/usersSlice"
import { UserPicker } from "../../../users/UserPicker"
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { TeamMemberRoleMenuField } from "./TeamMemberRoleMenuField"
import { addDocumentTeamMember, DocumentTeamMember, DocumentTeamMemberRole, substituteDocumentTeamMember } from "../teamMemberApi"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { selectDocument } from "../../documentSlice"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"

export interface AddEditTeamMemberDialog {
    documentId: AppId
    mode: "create" | "edit" | "substitute"
    userToSubstitute: string
    excludedUsers: Set<string>
    onSuccessfulSave: (teamMember: DocumentTeamMember) => void
    onCancelClick: () => void,
}
export const AddEditTeamMemberDialog = ({ documentId, mode, userToSubstitute, onCancelClick, onSuccessfulSave, excludedUsers }: AddEditTeamMemberDialog) => {
    const dispatch = useAppDispatch()
    const documentStatus = useAppSelector(selectDocument)?.doc?.header.status
    const [user, setUser] = useState<User | null>(() => mode != "substitute" ? (selectUserByName(store.getState(), userToSubstitute) ?? null) : null)
    const [role, setRole] = useState<DocumentTeamMemberRole | undefined>(() => {
        if (documentStatus === 'O') return 'R'
        if (documentStatus === 'I') return 'A'
        return undefined
    })
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")


    const handleUserRoleChange: AppTextFieldHandler = useCallback(e => setRole(e.target.value as DocumentTeamMemberRole), [setRole])


    const isExcluded = useCallback((u: User) => excludedUsers.has(u.userName) && u.userName !== userToSubstitute, [excludedUsers, userToSubstitute])

    const handleOkClick = async () => {
        if (documentId && user && role) {
            setSaving(true)
            try {
                switch (mode) {
                    case "create":

                        const result = await dispatch(addDocumentTeamMember({ documentId: documentId, userName: user.userName, role: role }))
                        if (addDocumentTeamMember.fulfilled.match(result)) {
                            const newTeamMember = result.payload as DocumentTeamMember;
                            dispatch(showSuccess("saved"))
                            onSuccessfulSave(newTeamMember)
                        } else {
                            let errorMsg = "error"
                            if (result.payload && result.payload.kind === 'http') {
                                const problem = result.payload.problem
                                if (problem) {
                                    errorMsg = problem.title

                                }
                            }
                            setServerError(errorMsg)
                        }

                        break
                    case "edit":
                        break
                    case "substitute":
                        const result2 = await dispatch(substituteDocumentTeamMember({ documentId: documentId, userName: userToSubstitute, substitution: user.userName }))
                        if (substituteDocumentTeamMember.fulfilled.match(result2)) {
                            const newTeamMember = result2.payload as DocumentTeamMember;
                            dispatch(showSuccess("saved"))
                            onSuccessfulSave(newTeamMember)
                        }
                        else {
                            let errorMsg = "error"
                            if (result2.payload && result2.payload.kind === 'http') {
                                const problem = result2.payload.problem
                                if (problem) {
                                    errorMsg = problem.title

                                }
                            }
                            setServerError(errorMsg)
                        }
                        break


                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }

        }
        setSaving(false)
    }

    return <>
        <Dialog key="addEditTeamMemberDialog" open={true} maxWidth="sm" fullWidth keepMounted={false}>
            <DialogTitle>
                <If condition={mode === "create"}>
                    <Localized id="document-add-team-member">
                        <span>_Nowy członek zespołu</span>
                    </Localized>
                </If>
                <If condition={mode === "edit"}>
                    <Localized id="document-edit-team-member">
                        <span>_Edycja członka zespołu</span>
                    </Localized>
                </If>
                <If condition={mode === "substitute"}>
                    <Localized id="document-appoint-substitution" vars={{ userName: userToSubstitute }}>
                        <span>_Wyznacz zastępstwo</span>
                    </Localized>
                </If>
            </DialogTitle>
            <DialogContent sx={{
                padding: 2,
            }}>
                <UserPicker
                    sx={{ marginTop: 3 }}
                    value={user?.id ?? null}
                    onChange={setUser}
                    exclude={isExcluded}
                />
                <TeamMemberRoleMenuField
                    value={role}
                    onChange={handleUserRoleChange}
                />
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>_Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleOkClick} />
                <CancelButton loading={saving} onClick={onCancelClick} />
            </DialogActions>
        </Dialog>
    </>
}
